import { inkPoints, WHITEBOARD_COLORS, type InkPoint, type WhiteboardStroke } from "../../shared/whiteboard";

/**
 * Draws one stroke in board units (the caller sets the transform). `upTo` limits it
 * to the first n points, for replay.
 *
 * Ink: each piece of the smoothed curve (quadratic through the midpoints) is drawn
 * at its own width with round ends, so the line swells and thins like a nib.
 * Marker: one translucent path multiplied onto the board, so overlaps darken the way
 * a real highlighter does.
 */
export function drawStroke(ctx: CanvasRenderingContext2D, s: WhiteboardStroke, upTo?: number): void {
  const all = inkPoints(s);
  const pts = upTo === undefined ? all : all.slice(0, Math.max(1, upTo));
  if (!pts.length) return;
  const color = WHITEBOARD_COLORS[s.color] ?? WHITEBOARD_COLORS.graphite;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  if (s.tool === "marker") {
    ctx.globalAlpha = 0.34;
    ctx.globalCompositeOperation = "multiply";
    ctx.lineWidth = pts[0]!.w;
    ctx.beginPath();
    curve(ctx, pts);
    ctx.stroke();
  } else if (pts.length === 1) {
    ctx.beginPath();
    ctx.arc(pts[0]!.x, pts[0]!.y, Math.max(0.8, pts[0]!.w * 0.6), 0, Math.PI * 2);
    ctx.fill();
  } else {
    const mid = (a: InkPoint, b: InkPoint) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    let from = { x: pts[0]!.x, y: pts[0]!.y };
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i - 1]!;
      const to = i < pts.length - 1 ? mid(pts[i]!, pts[i - 1]!) : pts[i]!;
      ctx.beginPath();
      ctx.lineWidth = (p.w + pts[i]!.w) / 2;
      ctx.moveTo(from.x, from.y);
      if (i === 1) ctx.lineTo(to.x, to.y);
      else ctx.quadraticCurveTo(p.x, p.y, to.x, to.y);
      ctx.stroke();
      from = to;
    }
  }
  ctx.restore();
}

function curve(ctx: CanvasRenderingContext2D, pts: InkPoint[]): void {
  ctx.moveTo(pts[0]!.x, pts[0]!.y);
  if (pts.length === 1) {
    ctx.lineTo(pts[0]!.x + 0.1, pts[0]!.y);
    return;
  }
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i]!;
    const n = pts[i + 1]!;
    ctx.quadraticCurveTo(p.x, p.y, (p.x + n.x) / 2, (p.y + n.y) / 2);
  }
  const last = pts[pts.length - 1]!;
  ctx.lineTo(last.x, last.y);
}

/** How many of a stroke's points are drawn `elapsed` ms into its own (replay-speed) timeline. */
export function pointsAt(s: WhiteboardStroke, elapsed: number, speed: number): number {
  let t = 0;
  const n = s.pts.length / 3;
  for (let i = 1; i < n; i++) {
    t += s.pts[i * 3 + 2]! / speed;
    if (t > elapsed) return i;
  }
  return n;
}
